import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeaderV2 from "@/components/v2/SiteHeaderV2";
import { PRO_TOOLS, EXPERIENCES } from "@/data/v2Config";
import { ArrowUpRight, Wrench, Lock, GraduationCap } from "lucide-react";

const StatusBadge = ({ status }) =>
  status === "available" ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">Available</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Coming soon</span>
  );

export default function ProHub() {
  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeaderV2 />

      {/* Hero */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-brand mb-4">
            <Wrench className="w-4 h-4" /> {EXPERIENCES.pro.label}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-[1] max-w-2xl">
            Turn what you learned into <span className="text-brand">working business tools.</span>
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
            {EXPERIENCES.pro.blurb} Learn the concept in a mission first, then use a Pro tool to put it to work.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 rounded-full bg-white/5 border border-white/10 px-4 py-2">
            <GraduationCap className="w-4 h-4 text-brand" /> Education first, automation second.
          </div>
        </div>
      </section>

      {/* Tools grid */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="mb-8">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Pro toolkit</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Business implementation tools</h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pro-tools-grid">
          {PRO_TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={t.key}
                className="min-w-0"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 3) * 0.05 }}
              >
                <Link
                  to={`/v2/pro/${t.key}`}
                  data-testid={`pro-tool-card-${t.key}`}
                  className="group block h-full bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-slate-900 group-hover:bg-brand transition-colors flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                  <h3 className="font-heading text-base font-bold text-slate-900 leading-tight">{t.label}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand">
                    {t.status === "available" ? "Open tool" : "Preview"}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Pathfinder</span>
          <Link to="/v2" className="font-mono hover:text-slate-900">← RouteHaul Community</Link>
        </div>
      </footer>
    </div>
  );
}
